import React, { useState } from "react";
import { cn } from "~/utils/fontsConfig";

const GlassButton = ({
    children = "Generate",
    className,
    onClick,
    variant = "light",
    size = "md",
}: {
    children?: React.ReactNode;
    className?: string;
    onClick?: () => void;
    variant?: "light" | "dark";
    size?: "xs" | "md" | "lg";
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isActive, setIsActive] = useState(false);

    // Color configurations based on variant
    const colors = {
        light: {
            gradientFrom: "rgba(255,255,255,0.05)",
            gradientVia: "rgba(255,255,255,0.2)",
            gradientTo: "rgba(255,255,255,0.05)",
            shadowInner: "rgba(0,0,0,0.05)",
            shadowInnerBottom: "rgba(255,255,255,0.5)",
            shadowOuter: "rgba(0,0,0,0.2)",
            shadowGlow: "rgba(255,255,255,0.2)",
            hoverShadowGlow: "rgba(255,255,255,0.5)",
            activeShadowBottom: "rgba(255,255,255,0.75)",
            activeShadowInner: "rgba(0,0,0,0.15)",
            textShadow: "rgba(0,0,0,0.1)",
            textShadowHover: "rgba(0,0,0,0.12)",
            borderColor: "rgba(255,255,255,0.5)",
            reflectionColor: "rgba(255,255,255,0.5)",
            conicGradientColor: "rgba(0,0,0,0.5)",
            bottomShadowColor: "rgba(0,0,0,0.2)",
            reflectionGradientColor: "rgba(255,255,255,0.5)",
            reflectionGradientStart: "rgba(255,255,255,0)",
            reflectionGradientEnd: "rgba(255,255,255,0)",
        },
        dark: {
            gradientFrom: "rgba(44, 44, 44, 0.2)",
            gradientVia: "rgba(44, 44, 44, 0.3)",
            gradientTo: "rgba(44, 44, 44, 0.2)",
            shadowInner: "rgba(0,0,0,0.2)",
            shadowInnerBottom: "rgba(255,255,255,0.1)",
            shadowOuter: "rgba(0,0,0,0.4)",
            shadowGlow: "rgba(0,0,0,0.3)",
            hoverShadowGlow: "rgba(255,255,255,0.15)",
            activeShadowBottom: "rgba(255,255,255,0.2)",
            activeShadowInner: "rgba(0,0,0,0.3)",
            textShadow: "rgba(0,0,0,0.3)",
            textShadowHover: "rgba(0,0,0,0.4)",
            borderColor: "rgba(255,255,255,0.2)",
            reflectionColor: "rgba(0, 0, 0, 0.15)",
            conicGradientColor: "rgba(255, 255, 255, 0.3)",
            bottomShadowColor: "rgba(0,0,0,0.4)",
            reflectionGradientColor: "rgba(91, 91, 91, 0.2)",
            reflectionGradientStart: "rgba(91, 91, 91, 0)",
            reflectionGradientEnd: "rgba(91, 91, 91, 0)",
        },
    };
    const sizes = {
        xs: {
            padding: "px-3 py-2.5",
            font: "text-xs",
            rounded: "rounded-lg",
        },
        md: {
            padding: "px-4 py-2.5",
            font: "text-md",
            rounded: "rounded-full",
        },
        lg: {
            padding: "px-6 py-3.5",
            font: "text-[1em] font-medium",
            rounded: "rounded-full",
        },
    };

    const colorSet = colors[variant];
    const sizeSet = sizes[size];

    return (
        <div
            className={cn(
                "h-max",
                "relative ",
                "duration-400 ease-[cubic-bezier(0.25,1,0.5,1)] group transition-all",
                "active:rotate-x-[25deg] active:transform",
                className,
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => {
                setIsHovered(false);
                setIsActive(false);
            }}
            onMouseDown={() => setIsActive(true)}
            onMouseUp={() => setIsActive(false)}
        >
            <button
                className={cn(
                    "duration-400 ease-[cubic-bezier(0.25,1,0.5,1)] relative cursor-pointer",
                    "border-none bg-gradient-to-r backdrop-blur-sm transition-all",
                    sizeSet.rounded,
                    "hover:scale-[0.975] hover:backdrop-blur-[0.01em]",
                    className,
                )}
                style={
                    {
                        WebkitTapHighlightColor: "rgba(0, 0, 0, 0)",
                        "--angle-1": isHovered ? "-125deg" : "-75deg",
                        "--angle-2": "-45deg",
                        "--border-width": "clamp(1px, 0.0625em, 4px)",
                        backgroundImage: `linear-gradient(to right, ${colorSet.gradientFrom}, ${colorSet.gradientVia}, ${colorSet.gradientTo})`,
                        boxShadow: isActive
                            ? `inset 0 2px 2px ${colorSet.shadowInner}, inset 0 -2px 2px ${colorSet.shadowInnerBottom}, 0 2px 2px -2px ${colorSet.shadowOuter}, 0 0 1.6px 4px inset ${colorSet.shadowGlow}, 0 3.6px 0.8px 0 ${colorSet.shadowOuter}, 0 4px 0 0 ${colorSet.activeShadowBottom}, inset 0 4px 0.8px 0 ${colorSet.activeShadowInner}`
                            : isHovered
                              ? `inset 0 2px 2px ${colorSet.shadowInner}, inset 0 -2px 2px ${colorSet.shadowInnerBottom}, 0 2.4px 0.8px -1.6px ${colorSet.shadowOuter}, 0 0 0.8px 1.6px inset ${colorSet.hoverShadowGlow}`
                              : `inset 0 2px 2px ${colorSet.shadowInner}, inset 0 -2px 2px ${colorSet.shadowInnerBottom}, 0 4px 2px -2px ${colorSet.shadowOuter}, 0 0 1.6px 4px inset ${colorSet.shadowGlow}`,
                    } as React.CSSProperties
                }
                onClick={onClick}
            >
                <span
                    className={cn(
                        "duration-400 ease-[cubic-bezier(0.25,1,0.5,1)] block flex select-none",
                        "items-center gap-2",
                        "tracking-[-0.05em]",
                        "transition-all",
                        sizeSet.padding,
                        sizeSet.font,
                    )}
                    style={{
                        fontSmooth: "antialiased",
                        textShadow: isHovered
                            ? `0.025em 0.025em 0.025em ${colorSet.textShadowHover}`
                            : `0em 0.25em 0.05em ${colorSet.textShadow}`,
                    }}
                >
                    {children}
                    <span
                        className={cn(
                            sizeSet.rounded,
                            "pointer-events-none absolute inset-0 mix-blend-screen",
                            "ease-[cubic-bezier(0.25,1,0.5,1)] transition-all duration-500",
                        )}
                        style={
                            {
                                top: "calc(0% + var(--border-width) / 2)",
                                left: "calc(0% + var(--border-width) / 2)",
                                width: "calc(100% - var(--border-width))",
                                height: "calc(100% - var(--border-width))",
                                backgroundImage: `linear-gradient(var(--angle-2), ${colorSet.reflectionGradientStart} 0%, ${colorSet.reflectionGradientColor} 40% 50%, ${colorSet.reflectionGradientEnd} 55%)`,
                                backgroundSize: "200% 200%",
                                backgroundPosition: isActive
                                    ? "50% 15%"
                                    : isHovered
                                      ? "25% 50%"
                                      : "0% 50%",
                                backgroundRepeat: "no-repeat",
                            } as React.CSSProperties
                        }
                    />
                </span>
                <span
                    className={cn(sizeSet.rounded, "absolute inset-0 block")}
                    style={
                        {
                            top: "calc(0% - var(--border-width) / 2)",
                            left: "calc(0% - var(--border-width) / 2)",
                            width: "calc(100% + var(--border-width))",
                            height: "calc(100% + var(--border-width))",
                            padding: "var(--border-width)",
                            mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                            maskComposite: "exclude",
                            boxShadow: `inset 0 0 0 calc(var(--border-width) / 2) ${colorSet.borderColor}`,
                        } as React.CSSProperties
                    }
                >
                    <span
                        className="absolute inset-0 transition-opacity duration-500"
                        style={{
                            opacity: isHovered ? 0 : 1,
                            background: `
                              conic-gradient(from -75deg at 50% 50%, ${colorSet.conicGradientColor}, rgba(0,0,0,0) 5% 40%, ${colorSet.conicGradientColor} 50%, rgba(0,0,0,0) 60% 95%, ${colorSet.conicGradientColor}),
                              linear-gradient(180deg, ${colorSet.reflectionColor}, ${colorSet.reflectionColor})
                            `,
                        }}
                    />
                    <span
                        className="absolute inset-0 transition-opacity duration-300"
                        style={{
                            opacity: isHovered ? 1 : 0,
                            background: `
                              conic-gradient(from -125deg at 50% 50%, ${colorSet.conicGradientColor}, rgba(0,0,0,0) 5% 40%, ${colorSet.conicGradientColor} 50%, rgba(0,0,0,0) 60% 95%, ${colorSet.conicGradientColor}),
                              linear-gradient(180deg, ${colorSet.reflectionColor}, ${colorSet.reflectionColor})
                            `,
                        }}
                    />
                </span>
            </button>
            <div
                className={cn(
                    "duration-400 ease-[cubic-bezier(0.25,1,0.5,1)]",
                    "absolute inset-0 blur-[clamp(2px,0.125em,12px)] transition-all",
                )}
                style={
                    {
                        "--shadow-cuttoff-fix": "2em",
                        width: "calc(100% + var(--shadow-cuttoff-fix))",
                        height: "calc(100% + var(--shadow-cuttoff-fix))",
                        top: "calc(0% - var(--shadow-cuttoff-fix) / 2)",
                        left: "calc(0% - var(--shadow-cuttoff-fix) / 2)",
                        pointerEvents: "none",
                        filter: isHovered
                            ? "blur(clamp(2px,0.0625em,6px))"
                            : "blur(clamp(2px,0.125em,12px))",
                    } as React.CSSProperties
                }
            >
                <div
                    className={cn(
                        sizeSet.rounded,
                        "duration-400 ease-[cubic-bezier(0.25,1,0.5,1)] absolute inset-0 transition-all",
                    )}
                    style={
                        {
                            background: `linear-gradient(180deg, ${colorSet.bottomShadowColor}, rgba(0,0,0,0.1))`,
                            width: "calc(100% - var(--shadow-cuttoff-fix) - 0.25em)",
                            height: "calc(100% - var(--shadow-cuttoff-fix) - 0.25em)",
                            top: isHovered
                                ? "calc(var(--shadow-cuttoff-fix) - 0.875em)"
                                : "calc(var(--shadow-cuttoff-fix) - 0.5em)",
                            left: "calc(var(--shadow-cuttoff-fix) - 0.875em)",
                            padding: "0.125em",
                            mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                            maskComposite: "exclude",
                            opacity: isActive ? 0.75 : 1,
                        } as React.CSSProperties
                    }
                />
            </div>
        </div>
    );
};

export default GlassButton;
